import React, { useEffect, useMemo, useState } from 'react';
import { useLocaleState, useNotify, useTranslate } from 'react-admin';
import { Box, Chip, FormHelperText, InputLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { createDirectoryClient, DirectoryPicker } from '@roommatik/directory-client';
import '@roommatik/directory-client/style.css';
import { API_CONFIG } from '@/config/constants';
import { resolveAuthToken } from '@/utils/httpClient';
import { linkAccountToRegistry } from '../../services/accountService';
import { englishPickerTexts } from './registryPickerTexts';

// The picker proxy lives under /api so the gateway route that carries the rest of the API carries it too.
const PICKER_PATH = `${new URL(API_CONFIG.BASE_URL, window.location.origin).origin}/api/directory/picker`;

type RegistryKind = 'property' | 'organization';

interface Props {
  /** Registry id chosen so far (create) or already stored (edit). */
  value: string | null;
  onChange?: (id: string | null) => void;
  /** Present on an existing account: picking links it at once and the link becomes read-only. */
  recordId?: string;
}

/**
 * Links an account to its record in the shared registry. Creating a record is not offered here:
 * the person chooses one that already exists.
 */
const RegistryLinkPicker: React.FC<Props> = ({ value, onChange, recordId }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const [kind, setKind] = useState<RegistryKind>('property');
  const [linked, setLinked] = useState<string | null>(recordId ? value : null);
  const [busy, setBusy] = useState(false);
  const immediate = Boolean(recordId);
  const locked = immediate && Boolean(linked);
  const current = immediate ? linked : value;

  // A record that is already linked can be a property or an organization: find out which, so the name is shown.
  useEffect(() => {
    if (!recordId || !value) return;
    let cancelled = false;
    const client = createDirectoryClient({ basePath: PICKER_PATH, getToken: resolveAuthToken, unwrap: true });
    (async () => {
      try {
        const asProperty = await client.get('property', value);
        if (cancelled) return;
        if (asProperty) return setKind('property');
        const asOrganization = await client.get('organization', value);
        if (!cancelled && asOrganization) setKind('organization');
      } catch {
        // The picker shows its own «unreachable» state.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [recordId, value]);

  const texts = useMemo(() => (locale === 'en' ? englishPickerTexts : undefined), [locale]);

  const handleKindChange = (next: RegistryKind | null) => {
    if (!next || next === kind) return;
    setKind(next);
    if (!immediate) onChange?.(null);
  };

  const handlePick = async (picked: { id: string } | null) => {
    if (!immediate) {
      onChange?.(picked?.id ?? null);
      return;
    }
    if (!picked?.id || !recordId) return;
    setBusy(true);
    try {
      await linkAccountToRegistry(recordId, picked.id);
      setLinked(picked.id);
      onChange?.(picked.id);
      notify('resources.accounts.registry.linked', { type: 'success' });
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      notify(
        status === 409 ? 'resources.accounts.registry.conflict' : 'resources.accounts.registry.failed',
        { type: 'error' }
      );
    } finally {
      setBusy(false);
    }
  };

  const label = translate('resources.accounts.registry.label');
  const helperKey = immediate
    ? locked ? 'locked' : 'optionalNow'
    : 'optional';

  return (
    <Box sx={{ width: '100%' }}>
      <InputLabel shrink sx={{ position: 'static', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {label}
        {locked && (
          <Chip size="small" color="success" variant="outlined" label={translate('resources.accounts.registry.chip')} />
        )}
      </InputLabel>
      {!locked && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={kind}
          onChange={(_, next: RegistryKind | null) => handleKindChange(next)}
          sx={{ mb: 1 }}
        >
          <ToggleButton value="property">{translate('resources.accounts.registry.kindProperty')}</ToggleButton>
          <ToggleButton value="organization">{translate('resources.accounts.registry.kindOrganization')}</ToggleButton>
        </ToggleButtonGroup>
      )}
      <DirectoryPicker
        key={kind}
        kind={kind}
        value={current}
        onChange={handlePick}
        basePath={PICKER_PATH}
        getToken={resolveAuthToken}
        unwrap
        canCreate={false}
        texts={texts}
        disabled={locked || busy}
        aria-label={label}
      />
      <FormHelperText>{translate(`resources.accounts.registry.${helperKey}`)}</FormHelperText>
    </Box>
  );
};

export default RegistryLinkPicker;
